// src/hooks/useTransactions.ts

import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFormatting } from '../context/FormattingContext';
import { Transaction, TransactionType } from '../types/Transaction';
import { buildQueryParams } from '../utils/buildQueryParams';
import { withForcedLedgerReadMode } from '../utils/ledgerReadMode';
import { authFetch } from '../lib/authFetch';

export interface Summary {
  income: number;
  expense: number;
  savings: {
    cumulative: number;
    inRange: number;
  };
  priorBalance: number;
  cashFlowBalance: number;
  netBalance: number;
  netWorth?: number;
  tentative?: {
    income: number;
    expense: number;
    savings: {
      cumulative: number;
    };
    netBalance: number;
    cashFlowBalance: number;
  };
}

const normalizeTentativeSummary = (summaryData: any): NonNullable<Summary['tentative']> => ({
  income: summaryData?.tentative?.income ?? 0,
  expense: summaryData?.tentative?.expense ?? 0,
  savings: {
    cumulative: summaryData?.tentative?.savings?.cumulative ?? 0,
  },
  netBalance: summaryData?.tentative?.netBalance ?? 0,
  cashFlowBalance: summaryData?.tentative?.cashFlowBalance ?? 0,
});

export interface TransactionFilters {
  type?: TransactionType;
  category?: string;
  startDate?: string;
  endDate?: string;
  isRecurring?: boolean;
  tentative?: boolean;
  tags?: string[]; // ✅ NEW

}

export type TransactionListMode = 'committed' | 'all' | 'tentative';

export const useTransactions = (
  refreshKey: number,
  filters: TransactionFilters = {},
  showFuture: boolean = false,
  listMode: TransactionListMode = 'committed'
) => {
  const { auth } = useAuth();
  const formatting = useFormatting();

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [summary, setSummary] = useState<Summary>({
    income: 0,
    expense: 0,
    savings: {
      cumulative: 0,
      inRange: 0,
    },
    priorBalance: 0,
    cashFlowBalance: 0,
    netBalance: 0,
    netWorth: 0,
  });

  const [loading, setLoading] = useState(true);

  // 👇 Track the latest request; ignore stale completions
  const reqCounterRef = useRef(0);
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);


  useEffect(() => {
    let cancelled = false;
    const myReqId = ++reqCounterRef.current;

    const fetchAll = async () => {
      setLoading(true);
      try {
        const {
          type,
          category,
          startDate,
          endDate,
          isRecurring,
          tags,
        } = filters;
        const txTentativeParam =
          listMode === 'all' ? undefined : listMode === 'tentative' ? true : false;

        const txQuery = buildQueryParams({
          type,
          category,
          startDate,
          endDate,
          isRecurring,
          showFuture,
          tentative: txTentativeParam,
          tags, // ✅ include tags here
        }, {
          clampEndDateToTodayWhenShowFutureFalse: true,
          timeZone: formatting.timeZone,
        });

        const summaryQuery = buildQueryParams({
          type,
          category,
          startDate,
          endDate,
          showFuture,
          isRecurring,
          tags, // 🔄 optional: comment out if not needed in summary
        }, {
          clampEndDateToTodayWhenShowFutureFalse: true,
          timeZone: formatting.timeZone,
          omitKeys: ['showFuture'],
        });

        const [txRes, summaryRes] = await Promise.all([
          authFetch(withForcedLedgerReadMode(`/api/transactions?${txQuery}`), {
            headers: { Authorization: `Bearer ${auth.token}` },
          }),
          authFetch(withForcedLedgerReadMode(`/api/transactions/summary?${summaryQuery}`), {
            headers: { Authorization: `Bearer ${auth.token}` },
          }),
        ]);

        // 🛡️ Bail if a newer request started
        if (cancelled || myReqId !== reqCounterRef.current) return;

        const txData = await txRes.json();
        const summaryData = await summaryRes.json();

        if (cancelled || myReqId !== reqCounterRef.current) return;

        //console.log('🧾 Transactions Response:', txData);
        //console.log('📊 Summary Response:', summaryData);
        //console.log('🔮 Tentative Summary Response:', tentativeData);

        setSummary({
          income: summaryData.income || 0,
          expense: summaryData.expense || 0,
          savings: {
            cumulative: summaryData.savings?.cumulative ?? 0,
            inRange: summaryData.savings?.inRange ?? 0,
          },
          priorBalance: summaryData.priorBalance ?? 0,
          cashFlowBalance: summaryData.cashFlowBalance || 0,
          netBalance: summaryData.netBalance || 0,
          netWorth: summaryData.netWorth ?? 0,
          tentative: normalizeTentativeSummary(summaryData),
        });

        setTransactions(
          Array.isArray(txData) ? txData : txData.transactions || []
        );
      } catch (err) {
        console.error('❌ useTransactions error:', err);
      } finally {
        if (!cancelled && myReqId === reqCounterRef.current) setLoading(false);
      }
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [auth.token, refreshKey, filtersKey, formatting.timeZone, showFuture, listMode]);

  return { transactions, summary, loading };
};
